import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';
import { improveAiErrorUserMessage } from '../lib/improveAiError';
import toast from 'react-hot-toast';
import {
  showElternbriefSendFinished,
  showElternbriefSendStarted
} from '../lib/massenmailSendFeedback';
import RecipientManager from '../components/RecipientManager';
import EmailCredentialModal from '../components/EmailCredentialModal';
import PersonalDataRequiredModal from '../components/PersonalDataRequiredModal';
import {
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
  EyeIcon,
  PaperClipIcon,
  ArrowUpTrayIcon,
  DocumentIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const MASSENMAIL_SEND_TIMEOUT_MS = 20 * 60 * 1000;

function startElternbriefSendRequest(elternbriefId: string | number) {
  showElternbriefSendStarted(elternbriefId);

  void api
    .post(`/massenmail/${elternbriefId}/send`, {}, { timeout: MASSENMAIL_SEND_TIMEOUT_MS })
    .then((response) => {
      if (response.data?.success) {
        showElternbriefSendFinished(
          elternbriefId,
          response.data.message || 'Elternbrief wurde versendet.',
          true
        );
      } else {
        showElternbriefSendFinished(
          elternbriefId,
          response.data?.error || 'Versand fehlgeschlagen',
          false
        );
      }
    })
    .catch((error: any) => {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Fehler beim Versenden';
      showElternbriefSendFinished(elternbriefId, msg, false);
    });
}

interface Module {
  id?: number;
  modul_typ:
    | 'lesebestaetigung'
    | 'umfrage'
    | 'texteingabe'
    | 'dateiupload'
    | 'lernbuerostatistik'
    | 'dateianhang'
    | 'terminbuchen'
    | 'zahlung'
    | 'elternportal_einladung';
  titel: string | null;
  konfiguration: any;
  position: number;
  pflichtfeld: boolean;
  fragen?: Frage[];
  anhaenge?: Attachment[];
}

interface AppointmentConfig {
  id: number;
  title: string;
  description: string | null;
  slot_duration_minutes: number;
  appointment_type: string;
  public_token: string;
  is_active: boolean;
}

interface Frage {
  id?: number;
  frage_text: string;
  frage_typ: 'single_choice' | 'multiple_choice' | 'freitext' | 'bewertung';
  optionen: string[];
  position: number;
  pflichtfeld: boolean;
  zufaellige_reihenfolge: boolean;
}

interface Student {
  sophomorixUnid: string;
  Vorname: string;
  Name: string;
  Klasse: string;
}

interface Attachment {
  id: number;
  dateiname: string;
  original_dateiname: string;
  mime_type: string;
  dateigroesse: number;
  hochgeladen_am?: string;
}

const MassenmailEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const parentPortalStatusQuery = useQuery(
    ['parent-portal-status'],
    async () => {
      const response = await api.get('/auth/parent-portal-status');
      return Boolean(response.data?.data?.enabled);
    },
    { staleTime: 60_000 }
  );
  const parentPortalEnabled = parentPortalStatusQuery.data !== false;
  const [searchParams] = useSearchParams();

  const isNewElternbrief = !id || id === 'neu';
  const appointmentConfigIdFromUrl = searchParams.get('appointment_config_id');

  const [titel, setTitel] = useState('');
  const [inhalt, setInhalt] = useState('');
  const [tokenGueltigTage, setTokenGueltigTage] = useState(30);
  const [module, setModule] = useState<Module[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [showRecipients, setShowRecipients] = useState(false);
  const [sendingAfterSave, setSendingAfterSave] = useState(false);
  const [sendingInProgress, setSendingInProgress] = useState(false);
  const [uploading, setUploading] = useState<Record<number, boolean>>({});
  const [aiLoading, setAiLoading] = useState(false);
  const [showEmailCredentialModal, setShowEmailCredentialModal] = useState(false);
  const [showPersonalDataModal, setShowPersonalDataModal] = useState(false);
  const pendingSendRef = useRef<null | (() => Promise<void>)>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [sharedBy, setSharedBy] = useState<{ first_name?: string; last_name?: string; username?: string } | null>(null);
  
  // Translation states
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [translationInfo, setTranslationInfo] = useState<{
    hasFamilyLanguages: boolean;
    familyLanguages: string[];
    canTranslate: boolean;
  } | null>(null);
  const [loadingTranslationInfo, setLoadingTranslationInfo] = useState(false);

  // Appointment configs state
  const [appointmentConfigs, setAppointmentConfigs] = useState<AppointmentConfig[]>([]);
  const [loadingAppointmentConfigs, setLoadingAppointmentConfigs] = useState(false);

  // Effect to add Terminbuchen module when appointment_config_id is in URL
  useEffect(() => {
    if (isNewElternbrief && appointmentConfigIdFromUrl && appointmentConfigs.length > 0) {
      const configId = parseInt(appointmentConfigIdFromUrl);
      const configExists = appointmentConfigs.find(c => c.id === configId && c.is_active);
      
      if (configExists && !module.find(m => m.modul_typ === 'terminbuchen' && m.konfiguration?.appointment_config_id === configId)) {
        // Add Terminbuchen module with the appointment config
        const newModule: Module = {
          modul_typ: 'terminbuchen',
          titel: null,
          konfiguration: {
            appointment_config_id: configId
          },
          position: module.length,
          pflichtfeld: false
        };
        setModule(prev => [...prev, newModule]);
        
        // Set default title and content if empty
        if (!titel.trim()) {
          const selectedConfig = appointmentConfigs.find(c => c.id === configId);
          setTitel(selectedConfig?.title || 'Terminbuchung');
          setInhalt(`Sehr geehrte Eltern,\n\nSie können unter folgendem Link einen Termin buchen:\n\n[Bitte klicken Sie auf den Link im Terminbuchen-Modul]`);
        }
        
        // Clean URL parameter
        navigate(`/elternbriefe/neu`, { replace: true });
        toast.success('Terminbuchen-Modul wurde automatisch hinzugefügt');
      }
    }
  }, [appointmentConfigIdFromUrl, appointmentConfigs, isNewElternbrief]);

  useEffect(() => {
    if (!isNewElternbrief && id) {
      loadElternbrief();
      loadRecipients();
      loadTranslationInfo();
    } else if (isNewElternbrief) {
      // Reset form when creating new Elternbrief
      setTitel('');
      setInhalt('');
      setTokenGueltigTage(30);
      setModule([]);
      setRecipients([]);
    }
    // Load appointment configs when component mounts
    loadAppointmentConfigs();
  }, [id, isNewElternbrief]);

  const loadElternbrief = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/massenmail/${id}`);
      if (response.data.success) {
        const data = response.data.data;
        setTitel(data.titel);
        setInhalt(data.inhalt);
        setTokenGueltigTage(data.token_gueltig_tage);
        setModule(data.module || []);
        setAutoTranslate(data.auto_translate || false);
        setIsReadOnly(data.shared_permission === 'read');
        setSharedBy(data.shared_by || null);
        setSendingInProgress(Boolean(data.sending));
      }
    } catch (error: any) {
      console.error('Error loading Elternbrief:', error);
      toast.error('Fehler beim Laden');
      navigate('/elternbriefe');
    } finally {
      setLoading(false);
    }
  };

  const loadRecipients = async (elternbriefId?: string) => {
    const targetId = elternbriefId ?? id;
    if (targetId && targetId !== 'neu') {
      try {
        const response = await api.get(`/massenmail/${targetId}/recipients`);
        if (response.data.success) {
          setRecipients(response.data.data);
          // Reload translation info when recipients change
          loadTranslationInfo();
        }
      } catch (error) {
        console.error('Error loading recipients:', error);
      }
    }
  };

  // Check for family languages whenever recipients change
  useEffect(() => {
    if (isNewElternbrief) {
      checkRecipientsForFamilyLanguages();
    }
  }, [recipients, isNewElternbrief]);

  const loadTranslationInfo = async () => {
    if (!id || id === 'neu') return;
    
    try {
      setLoadingTranslationInfo(true);
      const response = await api.get(`/massenmail/${id}/translation-info`);
      if (response.data.success) {
        const data = response.data.data;
        setTranslationInfo(data);
        // Auto-enable translation if family languages are found
        if (data.hasFamilyLanguages && data.canTranslate) {
          setAutoTranslate(true);
        }
      }
    } catch (error) {
      console.error('Error loading translation info:', error);
    } finally {
      setLoadingTranslationInfo(false);
    }
  };

  const checkRecipientsForFamilyLanguages = async () => {
    if (recipients.length === 0) {
      setTranslationInfo(null);
      return;
    }

    try {
      setLoadingTranslationInfo(true);
      
      // Get unique family languages from current recipients
      const unids = recipients.map(r => r.sophomorixUnid);
      const familyLanguages = new Set();
      
      // Check each recipient for family language
      for (const unid of unids) {
        try {
          const response = await api.get(`/email/family-language/${unid}`);
          if (response.data.success && response.data.data?.language) {
            familyLanguages.add(response.data.data.language);
          }
        } catch (error) {
          // Ignore individual errors
        }
      }
      
      const languagesArray = Array.from(familyLanguages);
      const hasFamilyLanguages = languagesArray.length > 0;
      
      // Check if OpenAI is configured (we can't easily check this from frontend, so assume true if we have family languages)
      const canTranslate = hasFamilyLanguages;
      
      setTranslationInfo({
        hasFamilyLanguages,
        familyLanguages: languagesArray as string[],
        canTranslate
      });
      
      // Auto-enable translation if family languages are found
      if (hasFamilyLanguages && canTranslate) {
        setAutoTranslate(true);
      }
    } catch (error) {
      console.error('Error checking recipients for family languages:', error);
    } finally {
      setLoadingTranslationInfo(false);
    }
  };

  const loadAppointmentConfigs = async () => {
    try {
      setLoadingAppointmentConfigs(true);
      const response = await api.get('/appointments/configs');
      if (response.data.success) {
        setAppointmentConfigs(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading appointment configs:', error);
      // Don't show error toast, just log it - user might not have any configs
    } finally {
      setLoadingAppointmentConfigs(false);
    }
  };

  const handleImproveText = async () => {
    if (!inhalt.trim()) {
      toast.error('Bitte geben Sie zuerst einen Text ein, der verbessert werden soll');
      return;
    }

    setAiLoading(true);
    try {
      const response = await api.post('/massenmail/improve-text', {
        text: inhalt
      });

      if (response.data.success) {
        setInhalt(response.data.data.improvedText);
        toast.success('Text wurde verbessert');
      } else {
        toast.error(
          improveAiErrorUserMessage({ response: { data: response.data } }, response.data.error || 'Text-Verbesserung fehlgeschlagen')
        );
      }
    } catch (error: unknown) {
      console.error('Error improving text:', error);
      toast.error(improveAiErrorUserMessage(error, 'Text-Verbesserung fehlgeschlagen'));
    } finally {
      setAiLoading(false);
    }
  };

  const handleModuleFileUpload = async (moduleIndex: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei ist zu groß. Maximale Größe: 10MB');
      return;
    }

    // If Elternbrief not saved yet, save it first
    let currentElternbriefId = id;
    let currentModule = module[moduleIndex];
    let savedModules: Module[] | null = null;
    
    if (isNewElternbrief || !currentModule?.id) {
      const loadingToast = toast.loading('Elternbrief wird gespeichert...');
      const saveResult = await handleSave(false);
      toast.dismiss(loadingToast);

      if (!saveResult?.id) {
        toast.error('Elternbrief muss gespeichert werden, bevor Dateien hochgeladen werden können');
        return;
      }

      currentElternbriefId = saveResult.id;
      savedModules = saveResult.module ?? null;

      if (isNewElternbrief) {
        navigate(`/elternbriefe/${saveResult.id}`, { replace: true });
      }

      const moduleFromSave = savedModules ?? module;
      currentModule = moduleFromSave[moduleIndex];

      if (!currentModule || !currentModule.id) {
        try {
          const refreshed = await api.get(`/massenmail/${saveResult.id}`);
          if (refreshed.data.success) {
            const refreshedModules: Module[] = refreshed.data.data.module || [];
            setModule(refreshedModules);
            currentModule = refreshedModules[moduleIndex];
          }
        } catch (refreshError) {
          console.error('Error refreshing modules after save:', refreshError);
        }
      }
    }

    try {
      setUploading(prev => ({ ...prev, [moduleIndex]: true }));
      
      // Ensure module has an ID
      if (!currentModule || !currentModule.id) {
        toast.error('Modul-ID fehlt. Bitte versuchen Sie es erneut.');
        return;
      }
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `/massenmail/${currentElternbriefId}/module/${currentModule.id}/upload-attachment`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        toast.success('Datei erfolgreich hochgeladen');
        
        // Reload module data. Use the latest state so a save that just assigned
        // module ids is not overwritten with the pre-save list.
        const attachmentsResponse = await api.get(`/massenmail/${currentElternbriefId}/module/${currentModule.id}/attachments`);
        if (attachmentsResponse.data.success) {
          const attachments = attachmentsResponse.data.data as Attachment[];
          const savedModuleId = currentModule.id;
          setModule((prev) => {
            const indexById = prev.findIndex((entry) => entry.id === savedModuleId);
            if (indexById >= 0) {
              const next = [...prev];
              next[indexById] = { ...next[indexById], anhaenge: attachments };
              return next;
            }

            const base = savedModules?.length ? savedModules : prev;
            const fallbackIndex = base.findIndex((entry) => entry.id === savedModuleId);
            const targetIndex = fallbackIndex >= 0 ? fallbackIndex : moduleIndex;
            if (!base[targetIndex]) return prev;
            const next = base.map((entry) => ({ ...entry }));
            next[targetIndex] = {
              ...next[targetIndex],
              id: next[targetIndex].id ?? savedModuleId,
              anhaenge: attachments
            };
            return next;
          });
        }
        
        // Reset file input
        event.target.value = '';
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Hochladen der Datei');
    } finally {
      setUploading(prev => ({ ...prev, [moduleIndex]: false }));
    }
  };

  const handleDeleteModuleAttachment = async (moduleIndex: number, attachmentId: number) => {
    if (!confirm('Möchten Sie diese Datei wirklich löschen?')) {
      return;
    }

    try {
      const response = await api.delete(
        `/massenmail/${id}/module/${module[moduleIndex].id}/attachment/${attachmentId}`
      );
      
      if (response.data.success) {
        toast.success('Datei erfolgreich gelöscht');
        
        // Reload module attachments
        const attachmentsResponse = await api.get(`/massenmail/${id}/module/${module[moduleIndex].id}/attachments`);
        if (attachmentsResponse.data.success) {
          const attachments = attachmentsResponse.data.data as Attachment[];
          setModule((prev) => {
            const next = [...prev];
            if (!next[moduleIndex]) return prev;
            next[moduleIndex] = { ...next[moduleIndex], anhaenge: attachments };
            return next;
          });
        }
      }
    } catch (error: any) {
      console.error('Error deleting attachment:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Löschen der Datei');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  interface SaveResult {
    id: string;
    module?: Module[];
  }

  const handleSave = async (andSend: boolean = false): Promise<SaveResult | null> => {
    if (!titel.trim()) {
      toast.error('Bitte geben Sie einen Titel ein');
      return null;
    }

    if (!inhalt.trim()) {
      toast.error('Bitte geben Sie einen Inhalt ein');
      return null;
    }

    try {
      setSaving(true);
      
      const payload = {
        titel,
        inhalt,
        token_gueltig_tage: tokenGueltigTage,
        module,
        auto_translate: autoTranslate,
        translation_languages: translationInfo?.familyLanguages || []
      };

      if (!isNewElternbrief && id) {
        const response = await api.put(`/massenmail/${id}`, payload);
        if (response.data.success) {
          const updatedModules: Module[] | undefined = response.data?.data?.module;
          if (updatedModules) {
            setModule(updatedModules);
          }
          toast.success('Elternbrief gespeichert');
          return { id, module: updatedModules };
        }
        toast.error(response.data?.message || 'Elternbrief konnte nicht gespeichert werden');
      } else {
        const response = await api.post('/massenmail', payload);
        if (response.data.success) {
          const newId = String(response.data.data.id);
          const moduleFromResponse: Module[] | undefined = response.data.data.module;
          let moduleForState: Module[] | undefined = moduleFromResponse;

          if (!moduleForState) {
            const moduleIds: number[] = response.data.data.moduleIds || [];
            if (moduleIds.length > 0) {
              moduleForState = module.map((mod, index) => ({
                ...mod,
                id: moduleIds[index] || mod.id
              }));
            }
          }

          if (moduleForState) {
            setModule(moduleForState);
          }

          // Persist recipients so they survive navigation (e.g. after file upload)
          if (recipients.length > 0) {
            try {
              const studentData = recipients.map((r) => ({
                unid: r.sophomorixUnid || r.schueler_unid
              })).filter((r) => r.unid);
              if (studentData.length > 0) {
                await api.post(`/massenmail/${newId}/recipients`, { schueler: studentData });
              }
            } catch (recipErr: any) {
              console.warn('Could not persist recipients on save:', recipErr);
            }
          }

          toast.success('Elternbrief gespeichert');

          if (!andSend) {
            navigate(`/elternbriefe/${newId}`);
          }
          return { id: newId, module: moduleForState };
        }
        toast.error(response.data?.message || 'Elternbrief konnte nicht gespeichert werden');
      }
      return null;
    } catch (error: any) {
      console.error('Error saving Elternbrief:', error);
      toast.error('Fehler beim Speichern');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    if (recipients.length === 0) {
      toast.error('Bitte fügen Sie mindestens einen Empfänger hinzu');
      return;
    }

    // Preflight: ensure sender name + email account
    const resume = async () => handleSaveAndSend();
    pendingSendRef.current = resume;
    try {
      const profRes = await api.get('/profile');
      const p = profRes.data?.data;
      const fn = (p?.firstName || '').toString().trim();
      const ln = (p?.lastName || '').toString().trim();
      const dn = (p?.displayName || '').toString().trim();
      const hasName = Boolean(`${fn} ${ln}`.trim()) || Boolean(dn);
      if (!hasName) {
        setShowPersonalDataModal(true);
        return;
      }
    } catch {
      // Non-blocking: fall back to backend username
    }
    try {
      const credRes = await api.get('/email/credentials');
      const creds = credRes.data?.data || [];
      const hasEnabled = creds.some((c: any) => c.enabled);
      if (!hasEnabled) {
        setShowEmailCredentialModal(true);
        return;
      }
    } catch {
      toast.error('E-Mail-Konten konnten nicht geprüft werden');
      return;
    } finally {
      pendingSendRef.current = null;
    }

    if (!confirm(`Elternbrief speichern und an ${recipients.length} Empfänger versenden?`)) {
      return;
    }

    try {
      setSendingAfterSave(true);

      const saveResult = await handleSave(true);
      if (!saveResult?.id) {
        setSendingAfterSave(false);
        return;
      }

      const elternbriefId = String(saveResult.id);
      startElternbriefSendRequest(elternbriefId);
      navigate('/elternbriefe');
    } catch (error: any) {
      console.error('Error sending Elternbrief:', error);
      const status = error?.response?.status;
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Fehler beim Speichern';
      if (status === 400 && typeof msg === 'string' && msg.toLowerCase().includes('email') && msg.toLowerCase().includes('credential')) {
        pendingSendRef.current = resume;
        setShowEmailCredentialModal(true);
        toast.error('Kein E-Mail-Konto konfiguriert. Bitte hinterlegen Sie es jetzt.');
        return;
      }
      toast.error(msg);
    } finally {
      setSendingAfterSave(false);
    }
  };

  const handleRecipientsAdded = async (newRecipients?: any[]) => {
    // While creating a new letter, accumulate selections locally until it is saved
    if (isNewElternbrief && newRecipients) {
      setRecipients((prevRecipients) => {
        const existingIds = new Set(
          prevRecipients.map((recipient) => recipient.sophomorixUnid || recipient.schueler_unid)
        );
        const mergedRecipients = [...prevRecipients];

        newRecipients.forEach((recipient) => {
          const recipientId = recipient.sophomorixUnid || recipient.schueler_unid;
          if (recipientId && !existingIds.has(recipientId)) {
            mergedRecipients.push(recipient);
            existingIds.add(recipientId);
          }
        });

        return mergedRecipients;
      });
      return;
    }
    
    // Reload recipients from server
    await loadRecipients();
  };

  const handleSend = async () => {
    if (recipients.length === 0) {
      toast.error('Bitte fügen Sie mindestens einen Empfänger hinzu');
      return;
    }

    // Preflight: ensure sender name + email account
    const resume = async () => handleSend();
    pendingSendRef.current = resume;
    try {
      const profRes = await api.get('/profile');
      const p = profRes.data?.data;
      const fn = (p?.firstName || '').toString().trim();
      const ln = (p?.lastName || '').toString().trim();
      const dn = (p?.displayName || '').toString().trim();
      const hasName = Boolean(`${fn} ${ln}`.trim()) || Boolean(dn);
      if (!hasName) {
        setShowPersonalDataModal(true);
        return;
      }
    } catch {
      // Non-blocking
    }
    try {
      const credRes = await api.get('/email/credentials');
      const creds = credRes.data?.data || [];
      const hasEnabled = creds.some((c: any) => c.enabled);
      if (!hasEnabled) {
        setShowEmailCredentialModal(true);
        return;
      }
    } catch {
      toast.error('E-Mail-Konten konnten nicht geprüft werden');
      return;
    } finally {
      pendingSendRef.current = null;
    }

    if (!confirm(`Elternbrief an ${recipients.length} Empfänger versenden?`)) {
      return;
    }

    if (!id) {
      toast.error('Elternbrief konnte nicht versendet werden');
      return;
    }

    startElternbriefSendRequest(id);
    navigate('/elternbriefe');
  };

  const addModule = (typ: Module['modul_typ']) => {
    if (typ === 'elternportal_einladung' && !parentPortalEnabled) {
      toast.error('Das Elternportal ist deaktiviert (Admineinstellungen).');
      return;
    }
    const defaultConfig =
      typ === 'elternportal_einladung'
        ? { gueltigkeit_tage: 14, info_text: '' }
        : {};
    const newModule: Module = {
      modul_typ: typ,
      titel: '',
      konfiguration: defaultConfig,
      position: module.length,
      pflichtfeld: false,
      fragen: typ === 'umfrage' ? [] : undefined
    };
    setModule([...module, newModule]);
  };

  const removeModule = (index: number) => {
    setModule(module.filter((_, i) => i !== index));
  };

  const updateModule = (index: number, updates: Partial<Module>) => {
    const newModule = [...module];
    newModule[index] = { ...newModule[index], ...updates };
    setModule(newModule);
  };

  const addFrage = (moduleIndex: number) => {
    const newModule = [...module];
    if (!newModule[moduleIndex].fragen) {
      newModule[moduleIndex].fragen = [];
    }
    
    const newFrage: Frage = {
      frage_text: '',
      frage_typ: 'single_choice',
      optionen: ['Option 1', 'Option 2'],
      position: newModule[moduleIndex].fragen!.length,
      pflichtfeld: false,
      zufaellige_reihenfolge: false
    };
    
    newModule[moduleIndex].fragen!.push(newFrage);
    setModule(newModule);
  };

  const updateFrage = (moduleIndex: number, frageIndex: number, updates: Partial<Frage>) => {
    const newModule = [...module];
    if (newModule[moduleIndex].fragen) {
      newModule[moduleIndex].fragen![frageIndex] = {
        ...newModule[moduleIndex].fragen![frageIndex],
        ...updates
      };
      setModule(newModule);
    }
  };

  const removeFrage = (moduleIndex: number, frageIndex: number) => {
    const newModule = [...module];
    if (newModule[moduleIndex].fragen) {
      newModule[moduleIndex].fragen = newModule[moduleIndex].fragen!.filter((_, i) => i !== frageIndex);
      setModule(newModule);
    }
  };

  const updateOption = (moduleIndex: number, frageIndex: number, optionIndex: number, value: string) => {
    const newModule = [...module];
    if (newModule[moduleIndex].fragen && newModule[moduleIndex].fragen![frageIndex].optionen) {
      newModule[moduleIndex].fragen![frageIndex].optionen[optionIndex] = value;
      setModule(newModule);
    }
  };

  const addOption = (moduleIndex: number, frageIndex: number) => {
    const newModule = [...module];
    if (newModule[moduleIndex].fragen && newModule[moduleIndex].fragen![frageIndex].optionen) {
      newModule[moduleIndex].fragen![frageIndex].optionen.push(`Option ${newModule[moduleIndex].fragen![frageIndex].optionen.length + 1}`);
      setModule(newModule);
    }
  };

  const removeOption = (moduleIndex: number, frageIndex: number, optionIndex: number) => {
    const newModule = [...module];
    if (newModule[moduleIndex].fragen && newModule[moduleIndex].fragen![frageIndex].optionen) {
      newModule[moduleIndex].fragen![frageIndex].optionen = newModule[moduleIndex].fragen![frageIndex].optionen.filter((_, i) => i !== optionIndex);
      setModule(newModule);
    }
  };

  const getModuleTypeName = (typ: Module['modul_typ']) => {
    const names = {
      lesebestaetigung: 'Lesebestätigung',
      umfrage: 'Umfrage',
      texteingabe: 'Texteingabe',
      dateiupload: 'Datei-Upload',
      lernbuerostatistik: 'Lernbürostatistik',
      dateianhang: 'Dateianhang',
      terminbuchen: 'Terminbuchen',
      zahlung: 'Zahlung',
      elternportal_einladung: 'Elternportal-Einladung'
    };
    return names[typ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  const canEdit = !isReadOnly;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/elternbriefe')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Zurück zur Übersicht
        </button>

        {sendingInProgress && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            Dieser Elternbrief wird gerade versendet. Bitte warten Sie auf die Fertig-Meldung und schließen Sie sie mit dem X.
          </div>
        )}

        {isReadOnly && (
          <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-800">
            Nur Ansicht · Geteilt von {sharedBy?.first_name || ''} {sharedBy?.last_name || ''} ({sharedBy?.username})
          </div>
        )}

        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">
            {isNewElternbrief ? 'Neuer Elternbrief' : 'Elternbrief bearbeiten'}
          </h1>

          {canEdit && (
            <div className="flex gap-3">
            <button
              onClick={() => setShowRecipientModal(true)}
              disabled={sendingInProgress}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <UserGroupIcon className="h-5 w-5" />
              Empfänger auswählen ({recipients.length})
            </button>

            <button
              onClick={() => handleSave(false)}
              disabled={saving || sendingAfterSave || sendingInProgress}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Speichere...' : 'Speichern'}
            </button>

            {recipients.length > 0 && (
              <button
                onClick={handleSaveAndSend}
                disabled={saving || sendingAfterSave || sendingInProgress}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
                {sendingAfterSave ? 'Speichere & versende...' : 'Speichern & Versenden'}
              </button>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Grunddaten</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titel *
              </label>
              <input
                type="text"
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="z.B. Elternabend Einladung Klasse 5a"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Inhalt *
                </label>
                <button
                  onClick={handleImproveText}
                  disabled={!inhalt.trim() || aiLoading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={!inhalt.trim() ? 'Bitte geben Sie zuerst einen Text ein' : 'Text mit KI verbessern'}
                >
                  {aiLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <SparklesIcon className="w-4 h-4" />
                  )}
                  Verbessern
                </button>
              </div>
              <textarea
                value={inhalt}
                onChange={(e) => setInhalt(e.target.value)}
                rows={8}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Sehr geehrte Eltern,&#10;&#10;..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token-Gültigkeit (Tage)
              </label>
              <input
                type="number"
                value={tokenGueltigTage}
                onChange={(e) => setTokenGueltigTage(parseInt(e.target.value) || 30)}
                min={1}
                max={365}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">
                Nach wie vielen Tagen läuft der Link für die Empfänger ab?
              </p>
            </div>
          </div>
          
          {/* Translation Settings */}
          {translationInfo && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="autoTranslate"
                  checked={autoTranslate}
                  onChange={(e) => setAutoTranslate(e.target.checked)}
                  disabled={!translationInfo.canTranslate}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <div className="flex-1">
                  <label htmlFor="autoTranslate" className="block text-sm font-medium text-gray-700">
                    Automatisch übersetzen
                    {translationInfo.hasFamilyLanguages && (
                      <span className="text-blue-600 ml-1">
                        ({translationInfo.familyLanguages.join(', ')})
                      </span>
                    )}
                  </label>
                  <p className="mt-1 text-sm text-gray-500">
                    {translationInfo.hasFamilyLanguages 
                      ? 'Übersetzt den Elternbrief automatisch in die Familiensprachen der Empfänger'
                      : 'Keine Empfänger mit Familiensprachen gefunden'
                    }
                  </p>
                  {!translationInfo.canTranslate && translationInfo.hasFamilyLanguages && (
                    <p className="mt-1 text-sm text-orange-600">
                      ⚠️ Übersetzung nicht verfügbar (OpenAI API nicht konfiguriert)
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modules */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Funktionsmodule</h2>
            
            {canEdit && (
              <div className="flex gap-2">
                <button
                  onClick={() => addModule('lesebestaetigung')}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  + Lesebestätigung
                </button>
                <button
                  onClick={() => addModule('umfrage')}
                  className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                >
                  + Umfrage
                </button>
                <button
                  onClick={() => addModule('texteingabe')}
                  className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                >
                  + Texteingabe
                </button>
                <button
                  onClick={() => addModule('lernbuerostatistik')}
                  className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                >
                  + Lernbürostatistik
                </button>
                <button
                  onClick={() => addModule('dateianhang')}
                  className="px-3 py-1 text-sm bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors"
                >
                  + Dateianhang
                </button>
                <button
                  onClick={() => addModule('terminbuchen')}
                  className="px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                >
                  + Terminbuchen
                </button>
                <button
                  onClick={() => addModule('zahlung')}
                  className="px-3 py-1 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors"
                >
                  + Zahlung
                </button>
                {parentPortalEnabled ? (
                  <button
                    onClick={() => addModule('elternportal_einladung')}
                    className="px-3 py-1 text-sm bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200 transition-colors"
                  >
                    + Elternportal-Einladung
                  </button>
                ) : null}
              </div>
            )}
          </div>

          {module.length === 0 && (
            <p className="text-gray-500 text-center py-8">
              Keine Module hinzugefügt. Fügen Sie Module hinzu, um Funktionen wie Lesebestätigung oder Umfragen zu nutzen.
            </p>
          )}

          <div className="space-y-4">
            {module.map((modul, moduleIndex) => (
              <div key={moduleIndex} className="border border-gray-300 rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-2">
                      {getModuleTypeName(modul.modul_typ)}
                    </h3>

                    <input
                      type="text"
                      value={modul.titel || ''}
                      onChange={(e) => updateModule(moduleIndex, { titel: e.target.value })}
                      placeholder="Optionaler Titel für dieses Modul"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {canEdit && (
                    <button
                      onClick={() => removeModule(moduleIndex)}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={modul.pflichtfeld}
                      value={modul.pflichtfeld ? 'true' : 'false'}
                      onChange={(e) => updateModule(moduleIndex, { pflichtfeld: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Pflichtfeld</span>
                  </label>
                </div>

                {/* Module-specific config */}
                {modul.modul_typ === 'lesebestaetigung' && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      Empfänger können bestätigen, dass sie den Brief gelesen haben (per Link-Klick und optional Checkbox).
                    </p>
                  </div>
                )}

                {modul.modul_typ === 'umfrage' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-700">Fragen</h4>
                      <button
                        onClick={() => addFrage(moduleIndex)}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Frage hinzufügen
                      </button>
                    </div>

                    {modul.fragen && modul.fragen.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Keine Fragen vorhanden
                      </p>
                    )}

                    {modul.fragen && modul.fragen.map((frage, frageIndex) => (
                      <div key={frageIndex} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 space-y-3">
                            <input
                              type="text"
                              value={frage.frage_text}
                              onChange={(e) => updateFrage(moduleIndex, frageIndex, { frage_text: e.target.value })}
                              placeholder="Frage eingeben..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />

                            <select
                              value={frage.frage_typ}
                              onChange={(e) => updateFrage(moduleIndex, frageIndex, { frage_typ: e.target.value as Frage['frage_typ'] })}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="single_choice">Einzelauswahl</option>
                              <option value="multiple_choice">Mehrfachauswahl</option>
                              <option value="freitext">Freitext</option>
                              <option value="bewertung">Bewertung (1-5)</option>
                            </select>

                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={frage.pflichtfeld}
                                  value={frage.pflichtfeld ? 'true' : 'false'}
                                  onChange={(e) => updateFrage(moduleIndex, frageIndex, { pflichtfeld: e.target.checked })}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Pflichtfrage</span>
                              </label>

                              {(frage.frage_typ === 'single_choice' || frage.frage_typ === 'multiple_choice') && (
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={frage.zufaellige_reihenfolge}
                                    value={frage.zufaellige_reihenfolge ? 'true' : 'false'}
                                    onChange={(e) => updateFrage(moduleIndex, frageIndex, { zufaellige_reihenfolge: e.target.checked })}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-700">Zufällige Reihenfolge</span>
                                </label>
                              )}
                            </div>
                          </div>

                          <button
                            onClick={() => removeFrage(moduleIndex, frageIndex)}
                            className="ml-3 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Options for single/multiple choice */}
                        {(frage.frage_typ === 'single_choice' || frage.frage_typ === 'multiple_choice') && (
                          <div className="mt-3 space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Optionen:</label>
                            {frage.optionen.map((option, optionIndex) => (
                              <div key={optionIndex} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={option}
                                  onChange={(e) => updateOption(moduleIndex, frageIndex, optionIndex, e.target.value)}
                                  className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                                <button
                                  onClick={() => removeOption(moduleIndex, frageIndex, optionIndex)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  disabled={frage.optionen.length <= 2}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => addOption(moduleIndex, frageIndex)}
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              + Option hinzufügen
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {modul.modul_typ === 'texteingabe' && (
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      Empfänger können eine freie Textantwort eingeben.
                    </p>
                  </div>
                )}

                {modul.modul_typ === 'dateianhang' && (
                  <div className="bg-teal-50 p-4 rounded-lg space-y-4">
                    <p className="text-sm text-gray-700">
                      Empfänger können Dateien von dieser Landingpage herunterladen.
                    </p>
                    
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        onChange={(e) => handleModuleFileUpload(moduleIndex, e)}
                        className="hidden"
                        id={`file-upload-${moduleIndex}`}
                        disabled={uploading[moduleIndex]}
                      />
                      <button
                        type="button"
                        onClick={() => document.getElementById(`file-upload-${moduleIndex}`)?.click()}
                        disabled={uploading[moduleIndex]}
                        className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                      >
                        <ArrowUpTrayIcon className="h-5 w-5" />
                        {uploading[moduleIndex] ? 'Wird hochgeladen...' : 'Datei hochladen'}
                      </button>
                      <span className="text-sm text-gray-600">
                        Max. 10MB • PDF, Word, Excel, PowerPoint, Bilder, Text, ZIP
                      </span>
                    </div>

                    {(!modul.id || isNewElternbrief) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800">
                          ℹ️ Der Elternbrief wird beim Upload automatisch gespeichert.
                        </p>
                      </div>
                    )}

                    {modul.anhaenge && modul.anhaenge.length > 0 ? (
                      <div className="space-y-2">
                        {modul.anhaenge.map((attachment) => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-3 bg-white border border-teal-200 rounded-lg"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <DocumentIcon className="h-6 w-6 text-teal-600 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 truncate text-sm">
                                  {attachment.original_dateiname}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {formatFileSize(attachment.dateigroesse)}
                                  {attachment.hochgeladen_am && (
                                    <span className="ml-2">
                                      • {new Date(attachment.hochgeladen_am).toLocaleDateString('de-DE')}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            
                            <button
                              onClick={() => handleDeleteModuleAttachment(moduleIndex, attachment.id)}
                              className="ml-3 p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                              title="Datei löschen"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Keine Dateien hochgeladen</p>
                    )}
                  </div>
                )}

                {modul.modul_typ === 'lernbuerostatistik' && (
                  <div className="bg-orange-50 p-4 rounded-lg space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Modus</label>
                      <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name={`modus-${moduleIndex}`}
                            value="aktuell"
                            checked={modul.konfiguration?.modus !== 'zeitfenster'}
                            onChange={() => updateModule(moduleIndex, { konfiguration: { ...modul.konfiguration, modus: 'aktuell', startDate: '', endDate: '' } })}
                          />
                          Aktuell
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name={`modus-${moduleIndex}`}
                            value="zeitfenster"
                            checked={modul.konfiguration?.modus === 'zeitfenster'}
                            onChange={() => updateModule(moduleIndex, { konfiguration: { ...modul.konfiguration, modus: 'zeitfenster' } })}
                          />
                          Zeitfenster
                        </label>
                      </div>
                    </div>

                    {modul.konfiguration?.modus === 'zeitfenster' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Startdatum</label>
                          <input
                            type="date"
                            value={modul.konfiguration?.startDate ?? ''}
                            onChange={(e) => updateModule(moduleIndex, { konfiguration: { ...modul.konfiguration, startDate: e.target.value } })}
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Enddatum</label>
                          <input
                            type="date"
                            value={modul.konfiguration?.endDate ?? ''}
                            onChange={(e) => updateModule(moduleIndex, { konfiguration: { ...modul.konfiguration, endDate: e.target.value } })}
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    )}

                    <p className="text-sm text-orange-800">
                      Die Lernbürostatistik wird automatisch beim Versand für jeden Empfänger berechnet.
                    </p>
                  </div>
                )}

                {modul.modul_typ === 'terminbuchen' && (
                  <div className="bg-indigo-50 p-4 rounded-lg space-y-4">
                    <p className="text-sm text-gray-700">
                      Empfänger erhalten einen Link zur Terminbuchungs-Landingpage. Wählen Sie eine Terminbuchungs-Konfiguration aus.
                    </p>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Terminbuchungs-Konfiguration *
                      </label>
                      {loadingAppointmentConfigs ? (
                        <div className="text-sm text-gray-500">Lade Konfigurationen...</div>
                      ) : appointmentConfigs.length === 0 ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <p className="text-sm text-yellow-800">
                            Keine Terminbuchungs-Konfigurationen gefunden. Bitte erstellen Sie zuerst eine Konfiguration unter <a href="/appointments" className="underline font-semibold">Terminbuchungen</a>.
                          </p>
                        </div>
                      ) : (
                        <select
                          value={modul.konfiguration?.appointment_config_id || ''}
                          onChange={(e) => updateModule(moduleIndex, { 
                            konfiguration: { 
                              ...modul.konfiguration, 
                              appointment_config_id: parseInt(e.target.value) || null 
                            } 
                          })}
                          className="w-full px-3 py-2 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">-- Bitte auswählen --</option>
                          {appointmentConfigs
                            .filter(config => config.is_active)
                            .map(config => (
                              <option key={config.id} value={config.id}>
                                {config.title} ({config.slot_duration_minutes} Min)
                              </option>
                            ))}
                        </select>
                      )}
                      
                      {modul.konfiguration?.appointment_config_id && (
                        <div className="mt-2 text-sm text-gray-600">
                          {(() => {
                            const selectedConfig = appointmentConfigs.find(
                              c => c.id === modul.konfiguration?.appointment_config_id
                            );
                            return selectedConfig ? (
                              <div className="bg-white border border-indigo-200 rounded-lg p-3 mt-2">
                                <p className="font-medium text-gray-900">{selectedConfig.title}</p>
                                {selectedConfig.description && (
                                  <p className="text-sm text-gray-600 mt-1">{selectedConfig.description}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                  Dauer: {selectedConfig.slot_duration_minutes} Minuten
                                </p>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {modul.modul_typ === 'zahlung' && (
                  <div className="bg-emerald-50 p-4 rounded-lg space-y-4">
                    <p className="text-sm text-gray-700">
                      Beim Versenden wird automatisch ein Zahlungsvorgang erstellt und pro Empfänger eine Forderung
                      angelegt.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Betrag je Forderung (EUR) *
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={modul.konfiguration?.amount ?? ''}
                          onChange={(e) =>
                            updateModule(moduleIndex, {
                              konfiguration: {
                                ...modul.konfiguration,
                                amount: e.target.value
                              }
                            })
                          }
                          placeholder="z. B. 25,00"
                          className="mt-1 w-full px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </label>

                      <label className="block text-sm font-medium text-gray-700">
                        Fällig bis (optional)
                        <input
                          type="date"
                          value={modul.konfiguration?.due_at ?? ''}
                          onChange={(e) =>
                            updateModule(moduleIndex, {
                              konfiguration: {
                                ...modul.konfiguration,
                                due_at: e.target.value
                              }
                            })
                          }
                          className="mt-1 w-full px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </label>
                    </div>

                    <label className="block text-sm font-medium text-gray-700">
                      Hinweistext für E-Mail/PDF (optional)
                      <textarea
                        rows={3}
                        value={modul.konfiguration?.description ?? ''}
                        onChange={(e) =>
                          updateModule(moduleIndex, {
                            konfiguration: {
                              ...modul.konfiguration,
                              description: e.target.value
                            }
                          })
                        }
                        placeholder="z. B. Bitte überweisen Sie bis zum angegebenen Termin."
                        className="mt-1 w-full px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </label>
                  </div>
                )}

                {modul.modul_typ === 'elternportal_einladung' && (
                  <div className="bg-cyan-50 p-4 rounded-lg space-y-4">
                    <p className="text-sm text-gray-700">
                      Beim Versand werden Empfänger ohne aktives Elternkonto automatisch eingeladen.
                      Der Registrierungslink wird in derselben E-Mail wie der Elternbrief-Link versendet.
                    </p>

                    <label className="block text-sm font-medium text-gray-700">
                      Gültigkeit in Tagen
                      <input
                        type="number"
                        min={1}
                        max={30}
                        value={modul.konfiguration?.gueltigkeit_tage ?? 14}
                        onChange={(e) =>
                          updateModule(moduleIndex, {
                            konfiguration: {
                              ...modul.konfiguration,
                              gueltigkeit_tage: Number(e.target.value) || 14
                            }
                          })
                        }
                        className="mt-1 w-full px-3 py-2 border border-cyan-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </label>

                    <label className="block text-sm font-medium text-gray-700">
                      Optionaler Hinweistext für Eltern
                      <textarea
                        rows={3}
                        value={modul.konfiguration?.info_text ?? ''}
                        onChange={(e) =>
                          updateModule(moduleIndex, {
                            konfiguration: {
                              ...modul.konfiguration,
                              info_text: e.target.value
                            }
                          })
                        }
                        placeholder="Optionaler Zusatzhinweis zur Portal-Einladung"
                        className="mt-1 w-full px-3 py-2 border border-cyan-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recipients List */}
        {recipients.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Empfänger ({recipients.length})
            </h2>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recipients.map((recipient, index) => {
                // Handle both temporary recipients (from selection) and saved recipients (from DB)
                const vorname = recipient.Vorname;
                const name = recipient.Name;
                const klasse = recipient.Klasse || recipient.klasse;
                const email = recipient.empfaenger_email || 'wird beim Speichern ermittelt';
                const status = recipient.antwort_status;
                
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">
                        {vorname} {name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {klasse} • {email}
                      </p>
                    </div>
                    {status && (
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          status === 'beantwortet' ? 'bg-green-100 text-green-800' :
                          status === 'geoeffnet' ? 'bg-blue-100 text-blue-800' :
                          status === 'gesperrt' ? 'bg-yellow-100 text-yellow-800' :
                          status === 'abgelaufen' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {status === 'beantwortet' ? 'Beantwortet' :
                           status === 'geoeffnet' ? 'Geöffnet' :
                           status === 'gesperrt' ? 'Gesperrt' :
                           status === 'abgelaufen' ? 'Abgelaufen' :
                           'Ausstehend'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
            <p className="text-gray-500">Noch keine Empfänger ausgewählt</p>
          </div>
        )}
      </div>

      {/* Recipient Manager Modal */}
      <RecipientManager
        elternbriefId={!isNewElternbrief && id ? parseInt(id, 10) : undefined}
        isOpen={showRecipientModal}
        onClose={() => setShowRecipientModal(false)}
        onRecipientsAdded={handleRecipientsAdded}
        selectedRecipientUnids={recipients
          .map((recipient: any) => recipient.sophomorixUnid || recipient.schueler_unid)
          .filter((unid: string | undefined): unid is string => Boolean(unid))}
      />

      <PersonalDataRequiredModal
        isOpen={showPersonalDataModal}
        onClose={() => {
          setShowPersonalDataModal(false);
          pendingSendRef.current = null;
        }}
        onSaved={async () => {
          setShowPersonalDataModal(false);
          const resumeFn = pendingSendRef.current;
          pendingSendRef.current = null;
          if (resumeFn) await resumeFn();
        }}
      />

      <EmailCredentialModal
        isOpen={showEmailCredentialModal}
        onClose={() => {
          setShowEmailCredentialModal(false);
          pendingSendRef.current = null;
        }}
        credential={null}
        requiredForSending={true}
        onSave={async (credentials) => {
          try {
            const resp = await api.post('/email/credentials', credentials);
            if (resp.data?.success) {
              toast.success('E-Mail-Konto erfolgreich hinzugefügt');
              setShowEmailCredentialModal(false);
              const resumeFn = pendingSendRef.current;
              pendingSendRef.current = null;
              if (resumeFn) await resumeFn();
            } else {
              toast.error(resp.data?.error || 'Fehler beim Speichern des E-Mail-Kontos');
            }
          } catch (e: any) {
            toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Fehler beim Speichern des E-Mail-Kontos');
          }
        }}
      />
    </div>
  );
};

export default MassenmailEditor;
